import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import {
	Button,
	Card,
	FormGroup,
	Intent,
	Spinner,
	Callout,
	Switch,
	Tag,
	Icon,
	NonIdealState,
} from "@blueprintjs/core";
import { IStore } from "redux/store";
import Select from "react-select";
import toolSelectors from "redux/app/tool/selectors";
import {
	getTenantsOptionList,
	getVesselsOptionList,
} from "redux/app/tool/utils";
import toolActions from "redux/app/tool/actions";
import { IOption, IVessel } from "redux/app/feedback/@types";
import {
	clearIndicatorStyles,
	controlStyles,
	dropdownIndicatorStyles,
	menuStyles,
	optionStyles,
	placeholderStyles,
} from "redux/app/feedback/utils";
import clsx from "clsx";
import { commonUtils } from "redux/_common";
import authConstants from "redux/app/auth/constants";

type OwnProps = {};
type FeatureFlagsProps = PropsFromRedux & OwnProps;

interface FeatureConfig {
	bdnDropdown: boolean | null;
	feedbackGlobal: boolean | null;
	wayshipCopilot: boolean | null;
}

interface VesselFeatureStatus {
	vesselId: string;
	vesselName: string;
	loading: boolean;
	config: FeatureConfig;
	error?: string;
}

function FeatureFlags(props: FeatureFlagsProps) {
	const [selectedTenant, setSelectedTenant] = useState<IOption | undefined>(
		undefined
	);
	const [selectedVessels, setSelectedVessels] = useState<IOption[]>([]);
	const [vesselStatuses, setVesselStatuses] = useState<
		Record<string, VesselFeatureStatus>
	>({});
	const [loading, setLoading] = useState(false);
	const [updating, setUpdating] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	useEffect(() => {
		props.loadTenantsList();
	}, []);

	useEffect(() => {
		if (selectedTenant?.value) {
			props.loadTenantSpecificVesselsList(selectedTenant.value);
			setSelectedVessels([]);
			setVesselStatuses({});
			setSuccessMessage(null);
			setErrorMessage(null);
		}
	}, [selectedTenant]);

	// If a vessel is de-selected, remove its cached status (and any lingering errors)
	useEffect(() => {
		setSuccessMessage(null);
		setErrorMessage(null);
		setVesselStatuses((prev) => {
			const selectedIds = new Set(selectedVessels.map((v) => v.value));
			const next: Record<string, VesselFeatureStatus> = {};
			for (const vesselId of Object.keys(prev)) {
				if (selectedIds.has(vesselId)) {
					next[vesselId] = prev[vesselId];
				}
			}
			return next;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedVessels.map((v) => v.value).join(",")]);

	const fetchFeatureStatus = async (vesselId: string, vesselName: string) => {
		setVesselStatuses((prev) => ({
			...prev,
			[vesselId]: {
				vesselId,
				vesselName,
				loading: true,
				config: {
					bdnDropdown: null,
					feedbackGlobal: null,
					wayshipCopilot: null,
				},
			},
		}));

		try {
			const response = await fetch(
				`${commonUtils.getAPIBaseUrl()}/api/feature-flags?vesselId=${vesselId}&action=get`,
				{
					headers: {
						[authConstants.headers.TOKEN]: props.authToken || "",
					},
				}
			);
			const data = await response.json();

			if (response.ok) {
				setVesselStatuses((prev) => ({
					...prev,
					[vesselId]: {
						vesselId,
						vesselName,
						loading: false,
						config: {
							bdnDropdown: data.cnrSettings?.hasFuelPropertiesDropdown ?? null,
							feedbackGlobal:
								data.feedbackConfiguration?.isFeedbackGlobal ?? null,
							wayshipCopilot:
								data.feedbackConfiguration?.isWayshipAIChatEnabled ?? null,
						},
					},
				}));
			} else {
				setVesselStatuses((prev) => ({
					...prev,
					[vesselId]: {
						vesselId,
						vesselName,
						loading: false,
						config: {
							bdnDropdown: null,
							feedbackGlobal: null,
							wayshipCopilot: null,
						},
						error: data.message || "Failed to fetch feature status",
					},
				}));
			}
		} catch (error) {
			setVesselStatuses((prev) => ({
				...prev,
				[vesselId]: {
					vesselId,
					vesselName,
					loading: false,
					config: {
						bdnDropdown: null,
						feedbackGlobal: null,
						wayshipCopilot: null,
					},
					error: "Network error",
				},
			}));
		}
	};

	const handleFetchFeatures = async () => {
		if (selectedVessels.length === 0) return;

		setLoading(true);
		setSuccessMessage(null);
		setErrorMessage(null);

		for (const vessel of selectedVessels) {
			await fetchFeatureStatus(vessel.value, vessel.label);
		}

		setLoading(false);
	};

	const featureLabels: Record<
		"bdnDropdown" | "feedbackGlobal" | "wayshipCopilot",
		string
	> = {
		bdnDropdown: "BDN Dropdown",
		feedbackGlobal: "Global Feedback",
		wayshipCopilot: "Wayship Copilot",
	};

	const updateFeatureFlag = async (
		vesselId: string,
		feature: "bdnDropdown" | "feedbackGlobal" | "wayshipCopilot",
		value: boolean
	) => {
		setUpdating(`${vesselId}-${feature}`);
		setSuccessMessage(null);
		setErrorMessage(null);

		try {
			const body: any = { vesselId };

			if (feature === "bdnDropdown") {
				body.cnrSettings = { hasFuelPropertiesDropdown: value };
			} else if (feature === "feedbackGlobal") {
				body.feedbackConfiguration = { isFeedbackGlobal: value };
			} else {
				body.feedbackConfiguration = { isWayshipAIChatEnabled: value };
			}

			const response = await fetch(
				`${commonUtils.getAPIBaseUrl()}/api/feature-flags`,
				{
					method: "PATCH",
					headers: {
						"Content-Type": "application/json",
						[authConstants.headers.TOKEN]: props.authToken || "",
					},
					body: JSON.stringify(body),
				}
			);

			const data = await response.json();

			if (response.ok) {
				setVesselStatuses((prev) => ({
					...prev,
					[vesselId]: {
						...prev[vesselId],
						config: {
							...prev[vesselId].config,
							[feature]: value,
						},
					},
				}));
				setSuccessMessage(
					`Feature "${featureLabels[feature]}" updated successfully for ${vesselStatuses[vesselId]?.vesselName}`
				);
			} else {
				setErrorMessage(data.message || "Failed to update feature flag");
			}
		} catch (error) {
			setErrorMessage("Network error while updating feature flag");
		} finally {
			setUpdating(null);
		}
	};

	const getFeatureStatusTag = (value: boolean | null) => {
		if (value === null) {
			return (
				<Tag minimal intent={Intent.NONE} className="ml-2">
					Not Set
				</Tag>
			);
		}
		return value ? (
			<Tag intent={Intent.SUCCESS} className="ml-2">Enabled</Tag>
		) : (
			<Tag intent={Intent.DANGER} className="ml-2">Disabled</Tag>
		);
	};

	return (
		<div className="min-h-[calc(100vh-98px)] bg-gray-50 p-6 md:p-8 lg:p-12">
			<div className="max-w-[1600px] mx-auto space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
							<Icon icon="flag" size={28} className="text-blue-600" />
							Feature Flags Manager
						</h1>
						<p className="text-gray-600">
							Manage and configure feature flags for individual vessels.
						</p>
					</div>
				</div>

				{successMessage && (
					<Callout intent={Intent.SUCCESS} icon="tick-circle" title="Success">
						{successMessage}
					</Callout>
				)}

				{errorMessage && (
					<Callout intent={Intent.DANGER} icon="error" title="Error">
						{errorMessage}
					</Callout>
				)}

				<Card elevation={1} className="bg-white">
					<div className="p-2">
						<h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
							<Icon icon="filter" className="text-gray-500" />
							Selection Criteria
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<FormGroup
								label="Select Tenant"
								labelInfo="(required)"
								className="mb-0"
							>
								<Select
									id="tenants"
									isClearable
									isSearchable
									key={"tenants" + selectedTenant?.value}
									placeholder={"Search tenants"}
									isDisabled={
										loading ||
										props.jobToLoadVesselsSpecificToTenant.isLoading
									}
									value={selectedTenant}
									options={getTenantsOptionList(props.tenants)}
									isLoading={props.jobToLoadTenants.isLoading}
									onChange={(newValue) => {
										setSelectedTenant(newValue ?? undefined);
									}}
									className="w-full text-sm"
									menuPosition="fixed"
									classNames={{
										option: ({ isFocused, isSelected }) =>
											clsx(
												isFocused && optionStyles.focus,
												isSelected && optionStyles.selected,
												optionStyles.base
											),
										dropdownIndicator: () => dropdownIndicatorStyles,
										menu: () => menuStyles,
										clearIndicator: () => clearIndicatorStyles,
										placeholder: () => placeholderStyles,
										control: ({ isFocused, isDisabled }) =>
											clsx(
												isFocused
													? controlStyles.focus
													: controlStyles.nonFocus,
												isDisabled ? controlStyles.disabled : controlStyles.base
											),
										valueContainer: ({ isDisabled }) =>
											clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
									}}
								/>
							</FormGroup>

							<FormGroup
								label="Select Vessels"
								labelInfo="(required, multi-select)"
								className="mb-0"
							>
								<Select
									id="vessels"
									isClearable
									isSearchable
									isMulti
									key={"vessels" + selectedVessels.map((v) => v.value).join(",")}
									placeholder={"Search and select vessels"}
									isDisabled={
										!selectedTenant?.value ||
										loading ||
										props.jobToLoadVesselsSpecificToTenant.isLoading
									}
									value={selectedVessels}
									options={getVesselsOptionList(props.vessels)}
									isLoading={props.jobToLoadVesselsSpecificToTenant.isLoading}
									onChange={(newValue) => {
										setSelectedVessels([...(newValue || [])]);
									}}
									className="w-full text-sm"
									menuPosition="fixed"
									classNames={{
										option: ({ isFocused, isSelected }) =>
											clsx(
												isFocused && optionStyles.focus,
												isSelected && optionStyles.selected,
												optionStyles.base
											),
										dropdownIndicator: () => dropdownIndicatorStyles,
										menu: () => menuStyles,
										clearIndicator: () => clearIndicatorStyles,
										placeholder: () => placeholderStyles,
										control: ({ isFocused, isDisabled }) =>
											clsx(
												isFocused
													? controlStyles.focus
													: controlStyles.nonFocus,
												isDisabled ? controlStyles.disabled : controlStyles.base
											),
										valueContainer: ({ isDisabled }) =>
											clsx("pl-1", isDisabled && "bg-gray-200 ml-2"),
									}}
								/>
							</FormGroup>
						</div>

						<div className="flex justify-end mt-6 pt-4 border-t border-gray-100">
							<Button
								intent={Intent.PRIMARY}
								icon="refresh"
								large
								onClick={handleFetchFeatures}
								disabled={selectedVessels.length === 0 || loading}
								loading={loading}
							>
								Fetch Feature Status
							</Button>
						</div>
					</div>
				</Card>

				{Object.keys(vesselStatuses).length > 0 ? (
					<div className="space-y-6">
						<h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 border-b border-gray-200 pb-2">
							<Icon icon="settings" className="text-gray-500" />
							Vessel Configurations
						</h2>
						
						<div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
							{Object.values(vesselStatuses).map((status) => (
								<Card key={status.vesselId} elevation={2} className="relative overflow-hidden border-t-4 border-t-blue-500">
									<div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
										<div className="flex items-center gap-3">
											<div className="p-2 bg-blue-50 rounded-lg">
												<Icon icon="widget" size={20} className="text-blue-600" />
											</div>
											<div>
												<h3 className="text-lg font-bold text-gray-800">
													{status.vesselName}
												</h3>
												<p className="text-xs text-gray-500 font-mono">ID: {status.vesselId}</p>
											</div>
										</div>
										{status.loading && <Spinner size={20} />}
									</div>

									{status.error ? (
										<NonIdealState
											icon="error"
											title="Error loading configuration"
											description={status.error}
											action={
												<Button 
													icon="refresh" 
													onClick={() => fetchFeatureStatus(status.vesselId, status.vesselName)}
												>
													Retry
												</Button>
											}
										/>
									) : status.loading ? (
										<div className="py-12 flex justify-center">
											<Spinner size={40} />
										</div>
									) : (
										<div className="space-y-4">
											{/* BDN Dropdown Feature */}
											<div className="bg-gray-50 rounded-xl p-4 transition-all hover:shadow-sm border border-transparent hover:border-gray-200">
												<div className="flex items-start justify-between">
													<div className="flex-1">
														<div className="flex items-center gap-2 mb-1">
															<Icon icon="tint" className="text-gray-500" />
															<h4 className="font-semibold text-gray-800">
																BDN Based Dropdown
															</h4>
															{getFeatureStatusTag(status.config.bdnDropdown)}
														</div>
														<p className="text-sm text-gray-600 mb-3">
															Enable/Disable fuel properties dropdown based on BDN
														</p>
													</div>
													<Switch 
														checked={status.config.bdnDropdown === true}
														disabled={updating === `${status.vesselId}-bdnDropdown`}
														large
														innerLabelChecked="ON"
														innerLabel="OFF"
														onChange={(e) => updateFeatureFlag(
															status.vesselId,
															"bdnDropdown",
															e.currentTarget.checked
														)}
														className="mt-1"
													/>
												</div>
												{updating === `${status.vesselId}-bdnDropdown` && (
													<div className="text-xs text-blue-600 flex items-center gap-1 mt-2">
														<Spinner size={12} intent={Intent.PRIMARY} /> Updating...
													</div>
												)}
											</div>

											{/* Feedback Configuration */}
											<div className="bg-gray-50 rounded-xl p-4 transition-all hover:shadow-sm border border-transparent hover:border-gray-200">
												<div className="flex items-start justify-between">
													<div className="flex-1">
														<div className="flex items-center gap-2 mb-1">
															<Icon icon="comment" className="text-gray-500" />
															<h4 className="font-semibold text-gray-800">
																Global Feedback
															</h4>
															{getFeatureStatusTag(status.config.feedbackGlobal)}
														</div>
														<p className="text-sm text-gray-600 mb-3">
															Show feedback tickets to all users
														</p>
													</div>
													<Switch 
														checked={status.config.feedbackGlobal === true}
														disabled={updating === `${status.vesselId}-feedbackGlobal`}
														large
														innerLabelChecked="ON"
														innerLabel="OFF"
														onChange={(e) => updateFeatureFlag(
															status.vesselId,
															"feedbackGlobal",
															e.currentTarget.checked
														)}
														className="mt-1"
													/>
												</div>
												{updating === `${status.vesselId}-feedbackGlobal` && (
													<div className="text-xs text-blue-600 flex items-center gap-1 mt-2">
														<Spinner size={12} intent={Intent.PRIMARY} /> Updating...
													</div>
												)}
											</div>

											{/* Wayship Copilot */}
											<div className="bg-gray-50 rounded-xl p-4 transition-all hover:shadow-sm border border-transparent hover:border-gray-200">
												<div className="flex items-start justify-between">
													<div className="flex-1">
														<div className="flex items-center gap-2 mb-1">
															<Icon icon="chat" className="text-gray-500" />
															<h4 className="font-semibold text-gray-800">
																Wayship Copilot
															</h4>
															{getFeatureStatusTag(status.config.wayshipCopilot)}
														</div>
														<p className="text-sm text-gray-600 mb-3">
															Show the AI support assistant alongside feedback ticketing
														</p>
													</div>
													<Switch
														checked={status.config.wayshipCopilot === true}
														disabled={updating === `${status.vesselId}-wayshipCopilot`}
														large
														innerLabelChecked="ON"
														innerLabel="OFF"
														onChange={(e) => updateFeatureFlag(
															status.vesselId,
															"wayshipCopilot",
															e.currentTarget.checked
														)}
														className="mt-1"
													/>
												</div>
												{updating === `${status.vesselId}-wayshipCopilot` && (
													<div className="text-xs text-blue-600 flex items-center gap-1 mt-2">
														<Spinner size={12} intent={Intent.PRIMARY} /> Updating...
													</div>
												)}
											</div>
										</div>
									)}
								</Card>
							))}
						</div>
					</div>
				) : (
					selectedVessels.length === 0 && (
						<NonIdealState
							icon="search"
							title="No Vessels Selected"
							description="Select a tenant and one or more vessels to view and manage their feature flags."
							className="py-12 bg-white rounded-lg shadow-sm border border-gray-100"
						/>
					)
				)}

				{/* Feature Descriptions */}
				{Object.keys(vesselStatuses).length > 0 && (
					<Card elevation={1} className="bg-white mt-8">
						<div className="p-2">
							<h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
								<Icon icon="info-sign" className="text-blue-500" />
								Feature Documentation
							</h2>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
								<div className="bg-blue-50 rounded-lg p-5 border-l-4 border-blue-500">
									<h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
										<Icon icon="tint" /> BDN Based Dropdown
									</h4>
									<p className="text-sm text-blue-800 leading-relaxed">
										When enabled, the fuel properties dropdown in CNR settings
										will be populated based on BDN (Bunker Delivery Note) data. 
										This helps ensure that fuel properties match delivery records.
									</p>
									<code className="block mt-3 text-xs bg-blue-100 p-2 rounded text-blue-900 font-mono">
										cnrSettings.hasFuelPropertiesDropdown
									</code>
								</div>
								<div className="bg-purple-50 rounded-lg p-5 border-l-4 border-purple-500">
									<h4 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
										<Icon icon="globe" /> Global Feedback
									</h4>
									<p className="text-sm text-purple-800 leading-relaxed">
										When set to ON, all users on the vessel will have visibility
										of feedback tickets submitted by others. When OFF, users can only
										see their own tickets or tickets assigned to them.
									</p>
									<code className="block mt-3 text-xs bg-purple-100 p-2 rounded text-purple-900 font-mono">
										feedbackConfiguration.isFeedbackGlobal
									</code>
								</div>
								<div className="bg-teal-50 rounded-lg p-5 border-l-4 border-teal-500">
									<h4 className="font-bold text-teal-900 mb-2 flex items-center gap-2">
										<Icon icon="chat" /> Wayship Copilot
									</h4>
									<p className="text-sm text-teal-800 leading-relaxed">
										When enabled, the Wayship Copilot AI support assistant is shown
										alongside the regular feedback ticketing system, so users can get
										AI-assisted help without replacing existing tickets.
									</p>
									<code className="block mt-3 text-xs bg-teal-100 p-2 rounded text-teal-900 font-mono">
										feedbackConfiguration.isWayshipAIChatEnabled
									</code>
								</div>
							</div>
						</div>
					</Card>
				)}
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const toolStore = toolSelectors._getStore(store);
	return {
		tenants: toolSelectors.getTenants(toolStore),
		vessels: toolSelectors.getVessels(toolStore),
		jobToLoadTenants: toolSelectors.jobToLoadTenants(toolStore),
		jobToLoadVesselsSpecificToTenant:
			toolSelectors.jobToLoadVesselsSpecificToTenant(toolStore),
		authToken: store.app.auth.authToken,
	};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadTenantsList() {
			dispatch(toolActions.commands.toolTenantListLoad());
		},
		loadTenantSpecificVesselsList(tenantId: string) {
			dispatch(toolActions.commands.toolTenantSpecificVesselLoad(tenantId));
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(FeatureFlags);
